if (!customElements.get('lc-guided-finder')) {
  class LcGuidedFinder extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-lc-guided-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-lc-guided-panel]'));
      this.disclosure = this.querySelector('[data-lc-guided-disclosure]');

      if (this.disclosure) this.setupStickyDisclosure();

      if (!this.tabs.length || !this.panels.length) return;

      this.setAttribute('data-enhanced', 'true');
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activate(tab.dataset.lcGuidedTab));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, tab));
      });

      const selected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      this.activate(selected.dataset.lcGuidedTab, false);
    }

    disconnectedCallback() {
      if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
      if (this.handleStickyChange) {
        window.removeEventListener('resize', this.handleStickyChange);
        window.removeEventListener('scroll', this.handleStickyChange);
        document.removeEventListener('keydown', this.handleDisclosureKeydown);
      }
      if (this.stickyResizeObserver) this.stickyResizeObserver.disconnect();
      if (this.sectionRoot) {
        this.sectionRoot.classList.remove('lc-guided-finder-section--sticky');
        this.sectionRoot.style.removeProperty('--lc-guided-sticky-top');
      }
    }

    setupStickyDisclosure() {
      this.sectionRoot = this.closest('.shopify-section');
      this.headerSection = document.querySelector('.header-section');
      if (!this.sectionRoot) return;

      this.sectionRoot.classList.add('lc-guided-finder-section--sticky');
      this.updateStickyOffset = () => {
        this.stickyFrame = null;
        const headerBottom = this.headerSection ? this.headerSection.getBoundingClientRect().bottom : 0;
        this.sectionRoot.style.setProperty('--lc-guided-sticky-top', `${Math.max(0, Math.round(headerBottom))}px`);
      };
      this.handleStickyChange = () => {
        if (this.stickyFrame) return;
        this.stickyFrame = requestAnimationFrame(this.updateStickyOffset);
      };
      this.handleDisclosureKeydown = (event) => {
        if (event.key !== 'Escape' || !this.disclosure.open) return;
        this.disclosure.open = false;
        this.disclosure.querySelector('summary')?.focus();
      };

      this.updateStickyOffset();
      window.addEventListener('resize', this.handleStickyChange, { passive: true });
      window.addEventListener('scroll', this.handleStickyChange, { passive: true });
      document.addEventListener('keydown', this.handleDisclosureKeydown);

      if ('ResizeObserver' in window && this.headerSection) {
        this.stickyResizeObserver = new ResizeObserver(this.handleStickyChange);
        this.stickyResizeObserver.observe(this.headerSection);
      }
    }

    activate(key, moveFocus = false) {
      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.lcGuidedTab === key;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
        if (isActive && moveFocus) tab.focus();
      });

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.lcGuidedPanel === key;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });
    }

    onKeydown(event, currentTab) {
      const supportedKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!supportedKeys.includes(event.key)) return;

      event.preventDefault();
      const currentIndex = this.tabs.indexOf(currentTab);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;

      this.activate(this.tabs[nextIndex].dataset.lcGuidedTab, true);
    }
  }

  customElements.define('lc-guided-finder', LcGuidedFinder);
}
